import { OrderDto, ShoppingCartsApi } from '@eurogast/hub-client-typescript';
import { ApiService } from '@/services/ApiService';
import {
    DefaultCartDeletedItemDto,
    DefaultCartDto,
    DefaultCartItemDto,
    OrderSubmitRequestDto
} from '@/api-aliases';
import { Cart, CartItem, DeletedCartItem } from '@/services/cart/model/cart';
import { CartApi } from '@/services/cart/api/CartApi';

export class DefaultCartApiWrapper implements CartApi {
    private static instance: DefaultCartApiWrapper = new DefaultCartApiWrapper();

    public static singleton(): DefaultCartApiWrapper {
        return this.instance;
    }

    private cachedApi: ShoppingCartsApi;

    /**
     * Lateinit singleton-init for the actual shopping-cart-api
     *
     * NOTE: This needs to be lateinit! Otherwise it is constructed
     * before the ConfigService which causes the ApiService to crash.
     */
    private get api(): ShoppingCartsApi {
        if (this.cachedApi === undefined) {
            this.cachedApi = ApiService.wrap(ShoppingCartsApi);
        }
        return this.cachedApi;
    }

    private constructor() {
    }

    public async addAllItemsToOwnShoppingCart(
        tenant: string,
        customerId: string,
        item: CartItem[]
    ): Promise<Cart> {
        const apiItems = item.map((el) => this.toApi(el));

        const apiCart = await this.api.addAllItemsToOwnShoppingCart(
            tenant,
            customerId,
            apiItems
        );

        return this.toStoreCart(apiCart);
    }

    public async addItemToOwnShoppingCart(
        tenant: string,
        customerId: string,
        item: CartItem
    ): Promise<Cart> {
        const apiItem = this.toApi(item);

        const apiCart = await this.api.addItemToOwnShoppingCart(
            tenant,
            customerId,
            apiItem
        );

        return this.toStoreCart(apiCart);
    }

    public async addItemToOwnShoppingCartNoDetail(
        tenant: string,
        customerId: string,
        item: CartItem
    ): Promise<CartItem> {
        const apiItem = this.toApi(item);

        const apiCart = await this.api.addItemToOwnShoppingCartNoDetail(
            tenant,
            customerId,
            apiItem
        );

        return this.toStoreCartItem(apiCart);
    }

    public async clearOwnShoppingCart(
        tenant: string,
        customerId: string,
    ): Promise<Cart> {
        const apiCart = await this.api.clearOwnShoppingCart(
            tenant,
            customerId
        );

        return this.toStoreCart(apiCart);
    }

    public async getOwnShoppingCart(
        tenant: string,
        customerId: string
    ): Promise<Cart> {
        const apiCart = await this.api.getOwnShoppingCart(
            tenant,
            customerId
        );

        return this.toStoreCart(apiCart);
    }

    public async removeItemFromOwnShoppingCart(
        tenant: string,
        customerId: string,
        item: CartItem
    ): Promise<Cart> {
        const apiItem = this.toApi(item);

        const apiCart = await this.api.removeItemFromOwnShoppingCart(
            tenant,
            customerId,
            apiItem
        );

        return this.toStoreCart(apiCart);
    }

    public async submitOwnShoppingCart(
        tenant: string,
        customerId: string,
        orderInformation?: OrderSubmitRequestDto
    ): Promise<OrderDto> {
        return this.api.submitOwnShoppingCart(
            tenant,
            customerId,
            orderInformation
        );
    }

    private toStoreCart(dto: DefaultCartDto): Cart {
        return new Cart(
            dto.customerId,
            dto.userId,
            dto.items.map((el) => this.toStoreCartItem(el)),
            dto.validSince,
            dto.deletedItems.map((el) => this.toStoreDeletedItem(el))
        );
    }

    private toStoreCartItem(dto: DefaultCartItemDto): CartItem {
        return new CartItem(
            dto.sku,
            dto.amount,
            dto.unitAbbreviation,
            dto.unit,
            dto.product,
            dto.lastUpdatedAt
        );
    }

    private toStoreDeletedItem(dto: DefaultCartDeletedItemDto): DeletedCartItem {
        return new DeletedCartItem(
            dto.sku,
            dto.unitAbbr,
            dto.deletedAt
        );
    }

    private toApi(cartItem: CartItem): DefaultCartItemDto {
        return {
            sku: cartItem.sku,
            amount: cartItem.amount,
            unitAbbreviation: cartItem.unitAbbreviation,
            unit: cartItem.unit,
            product: cartItem.product,
            lastUpdatedAt: cartItem.lastUpdatedAt,
        };
    }
}
